import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Icon,
  Segment,
  Message,
  Label,
} from "semantic-ui-react";
import OverdueSlipPreview from "./OverdueSlipPreview";
import BookSelectionForm from "./BookSelectionForm";
import UsePrintSlip from "./UsePrintSlip";
import { fineApi } from "@api/fine/fine";

const OverdueSlipModal = ({
  isOpen,
  selectedPatron,
  onClose,
  onOverrideUpdate,
  existingOverrideData,
  allowOverride = true,
}) => {
  const getInitialState = (patron) => {
    if (patron) {
      const loans = patron.loans;
      if (patron.overrideData) {
        return {
          selectedBooks: new Set(patron.overrideData.selectedBooks || []),
          totalAmount: patron.overrideData.totalAmount || 0,
          overrideAmounts: patron.overrideData.overrideAmounts || {},
          isOverrideMode: patron.overrideData.isOverrideMode || false,
          manualTotalOverride: patron.overrideData.manualTotalOverride || "",
          useManualTotal: patron.overrideData.useManualTotal || false,
        };
      }

      const loansWithFines = loans.filter((loan) => loan.calculatedFine > 0);
      if (loansWithFines.length > 0) {
        const selectedIndices = loans
          .map((loan, index) => (loan.calculatedFine > 0 ? index : null))
          .filter((index) => index !== null);

        const totalAmount = selectedIndices.reduce(
          (sum, index) => sum + loans[index].calculatedFine,
          0
        );

        const overrideAmounts = {};
        loans.forEach((loan, index) => {
          overrideAmounts[index] = loan.calculatedFine.toString();
        });

        return {
          selectedBooks: new Set(selectedIndices),
          totalAmount: totalAmount,
          overrideAmounts: overrideAmounts,
          isOverrideMode: false,
          manualTotalOverride: "",
          useManualTotal: false,
        };
      }
    }
    return {
      selectedBooks: new Set(),
      totalAmount: 0,
      overrideAmounts: {},
      isOverrideMode: false,
      manualTotalOverride: "",
      useManualTotal: false,
    };
  };

  const [state, setState] = useState(() => getInitialState(selectedPatron));
  const { printSlip } = UsePrintSlip(onOverrideUpdate);

  useEffect(() => {
    setState(getInitialState(selectedPatron));
  }, [selectedPatron]);

  useEffect(() => {
    if (isOpen && selectedPatron) {
      // Fetch override data from database
      fetchOverrideDataFromDatabase();
    }
  }, [isOpen, selectedPatron]);

  const fetchOverrideDataFromDatabase = async () => {
    if (!selectedPatron?.loans?.length) return;

    const overrideAmounts = {};
    const selectedBooks = new Set();
    let hasOverrides = false;

    for (let index = 0; index < selectedPatron.loans.length; index++) {
      const loan = selectedPatron.loans[index];
      const loanId = loan.metadata?.pid;

      if (loanId) {
        try {
          // Fetch fine from database
          const response = await fineApi.getFinesByLoanId(loanId);

          if (response.data?.hits?.length > 0) {
            const fine = response.data.hits.find(
              (f) => f.status === "active" || f.status === "unpaid"
            );

            if (fine) {
              const amount = parseFloat(fine.total_fine || 0);
              overrideAmounts[index] = amount.toString();

              if (fine.is_manually_overridden) {
                hasOverrides = true;
              }

              if (amount > 0 || fine.is_manually_overridden) {
                selectedBooks.add(index);
              }
            }
          }
        } catch (error) {
          console.error(`Error fetching fine for loan ${loanId}:`, error);
        }
      }
    }

    if (hasOverrides) {
      const totalAmount = Array.from(selectedBooks).reduce((sum, index) => {
        return sum + parseFloat(overrideAmounts[index] || 0);
      }, 0);

      setState({
        overrideAmounts,
        selectedBooks,
        totalAmount,
        isOverrideMode: false,
        manualTotalOverride: "",
        useManualTotal: false,
      });
    } else {
      // Use default initialization
      setState(getInitialState(selectedPatron));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleBookSelection = (bookIndex, checked) => {
    const {
      selectedBooks,
      overrideAmounts,
      useManualTotal,
      manualTotalOverride,
    } = state;
    const newSelectedBooks = new Set(selectedBooks);

    if (checked) {
      newSelectedBooks.add(bookIndex);
    } else {
      newSelectedBooks.delete(bookIndex);
    }

    let totalAmount = 0;
    if (!useManualTotal) {
      newSelectedBooks.forEach((index) => {
        // Handle 0 amount as valid
        const overrideAmount = parseFloat(overrideAmounts[index]);
        if (!isNaN(overrideAmount) && overrideAmount >= 0) {
          totalAmount += overrideAmount;
        }
      });
    } else {
      const manualTotal = parseFloat(manualTotalOverride);
      totalAmount = !isNaN(manualTotal) && manualTotal >= 0 ? manualTotal : 0;
    }

    setState((prevState) => ({
      ...prevState,
      selectedBooks: newSelectedBooks,
      totalAmount: totalAmount,
    }));
  };

  const handleOverrideAmountChange = (bookIndex, value) => {
    const { selectedBooks, useManualTotal } = state;
    const newOverrideAmounts = {
      ...state.overrideAmounts,
      [bookIndex]: value,
    };

    let totalAmount = state.totalAmount;
    if (!useManualTotal) {
      totalAmount = 0;
      selectedBooks.forEach((index) => {
        // Handle 0 amount as valid
        const overrideAmount = parseFloat(newOverrideAmounts[index]);
        if (!isNaN(overrideAmount) && overrideAmount >= 0) {
          totalAmount += overrideAmount;
        }
      });
    }

    setState((prevState) => ({
      ...prevState,
      overrideAmounts: newOverrideAmounts,
      totalAmount: totalAmount,
    }));
  };

  const handleManualTotalChange = (value) => {
    // Allow 0 as valid value
    const manualTotal = parseFloat(value);
    const validTotal =
      !isNaN(manualTotal) && manualTotal >= 0 ? manualTotal : 0;

    setState((prevState) => ({
      ...prevState,
      manualTotalOverride: value,
      totalAmount: prevState.useManualTotal
        ? validTotal
        : prevState.totalAmount,
    }));
  };

  const toggleManualTotal = (checked) => {
    const { selectedBooks, overrideAmounts, manualTotalOverride } = state;

    let totalAmount = 0;
    if (checked) {
      totalAmount = parseFloat(manualTotalOverride) || 0;
    } else {
      selectedBooks.forEach((index) => {
        const overrideAmount = parseFloat(overrideAmounts[index]) || 0;
        totalAmount += overrideAmount;
      });
    }

    setState((prevState) => ({
      ...prevState,
      useManualTotal: checked,
      totalAmount: totalAmount,
    }));
  };

  const resetToCalculated = () => {
    const { selectedBooks } = state;

    const overrideAmounts = {};
    selectedPatron.loans.forEach((loan, index) => {
      overrideAmounts[index] = loan.calculatedFine.toString();
    });

    let totalAmount = 0;
    selectedBooks.forEach((index) => {
      totalAmount += selectedPatron.loans[index].calculatedFine;
    });

    setState((prevState) => ({
      ...prevState,
      overrideAmounts: overrideAmounts,
      totalAmount: totalAmount,
      manualTotalOverride: totalAmount.toString(),
      useManualTotal: false,
    }));
  };

  const toggleOverrideMode = () => {
    setState((prevState) => ({
      ...prevState,
      isOverrideMode: !prevState.isOverrideMode,
    }));
  };

  const handleSelectAll = () => {
    const { overrideAmounts, useManualTotal, manualTotalOverride } = state;
    const allIndices = selectedPatron.loans.map((_, index) => index);

    let totalAmount = 0;
    if (useManualTotal) {
      totalAmount = parseFloat(manualTotalOverride) || 0;
    } else {
      allIndices.forEach((index) => {
        const overrideAmount = parseFloat(overrideAmounts[index]) || 0;
        totalAmount += overrideAmount;
      });
    }

    setState((prevState) => ({
      ...prevState,
      selectedBooks: new Set(allIndices),
      totalAmount: totalAmount,
    }));
  };

  const handleSelectNone = () => {
    setState((prevState) => ({
      ...prevState,
      selectedBooks: new Set(),
      totalAmount: prevState.useManualTotal
        ? parseFloat(prevState.manualTotalOverride) || 0
        : 0,
    }));
  };

  const getOverriddenAmounts = () => {
    const {
      selectedBooks,
      overrideAmounts,
      useManualTotal,
      manualTotalOverride,
    } = state;

    // Build a map by loan ID for better matching
    const overridesByLoanId = {};
    const overridesByIndex = {};

    selectedBooks.forEach((index) => {
      const loan = selectedPatron.loans[index];
      const loanId = loan.metadata?.pid || loan.loanId;
      const amount = parseFloat(overrideAmounts[index]) || 0;

      overridesByIndex[index] = amount;
      if (loanId) {
        overridesByLoanId[loanId] = amount;
      }
    });

    if (useManualTotal) {
      return {
        totalAmount: parseFloat(manualTotalOverride) || 0,
        useManualTotal: true,
        selectedBooks: Array.from(selectedBooks),
        overrideAmounts: overridesByIndex,
        overridesByLoanId: overridesByLoanId,
        manualTotalOverride: manualTotalOverride,
        isOverrideMode: state.isOverrideMode,
      };
    }

    return {
      individualAmounts: overridesByLoanId,
      overrideAmounts: overridesByIndex,
      overridesByLoanId: overridesByLoanId,
      totalAmount: state.totalAmount,
      useManualTotal: false,
      selectedBooks: Array.from(selectedBooks),
      manualTotalOverride: manualTotalOverride,
      isOverrideMode: state.isOverrideMode,
    };
  };

  const handleClose = async () => {
    const { isOverrideMode, useManualTotal } = state;

    if (isOverrideMode || useManualTotal) {
      const overrideData = {
        ...getOverriddenAmounts(),
        overrideAmounts: state.overrideAmounts,
        isOverrideMode: state.isOverrideMode,
        manualTotalOverride: state.manualTotalOverride,
        useManualTotal: state.useManualTotal,
      };

      // Wait for database update to complete
      await updateFineAmountsInDatabase(overrideData);
      onClose(); // Don't pass overrideData anymore
    } else {
      onClose();
    }
  };

  const updateFineAmountsInDatabase = async (overrideData) => {
    const { selectedBooks, overrideAmounts } = state;

    for (const bookIndex of selectedBooks) {
      const loan = selectedPatron.loans[bookIndex];
      const loanId = loan.metadata?.pid;

      // Find the fine record for this loan
      const fineRecord = selectedPatron.fineRecords?.find(
        (fine) => fine.loan_id === loanId
      );

      if (fineRecord && overrideAmounts[bookIndex] !== undefined) {
        // Check for undefined instead of truthy
        const newAmount = parseFloat(overrideAmounts[bookIndex]);

        // Allow 0
        if (!isNaN(newAmount) && newAmount >= 0) {
          const updateData = {
            total_fine: newAmount,
            calculated_fine: newAmount,
            is_manually_overridden: true,
            notes:
              newAmount === 0
                ? `Fine waived (overridden from ₱${fineRecord.total_fine} to ₱0.00)`
                : `Amount manually overridden from ₱${fineRecord.total_fine} to ₱${newAmount}`,
          };

          // Update the fine in the database
          await fineApi.update(fineRecord.id, updateData);
        }
      }
    }
  };

  const handlePrint = () => {
    const { selectedBooks, isOverrideMode, useManualTotal } = state;
    printSlip(
      selectedBooks,
      isOverrideMode,
      useManualTotal,
      getOverriddenAmounts
    );
  };

  const renderBookSelection = () => {
    const { selectedBooks, isOverrideMode, overrideAmounts, useManualTotal } =
      state;

    if (!selectedPatron || selectedPatron.loans.length === 0) {
      return null;
    }

    return (
      <Segment className="book-selection-segment">
        <BookSelectionForm
          selectedPatron={selectedPatron}
          selectedBooks={selectedBooks}
          isOverrideMode={isOverrideMode}
          overrideAmounts={overrideAmounts}
          useManualTotal={useManualTotal}
          manualTotalOverride={state.manualTotalOverride}
          onToggleOverrideMode={toggleOverrideMode}
          onToggleManualTotal={toggleManualTotal}
          onManualTotalChange={handleManualTotalChange}
          onResetToCalculated={resetToCalculated}
          onBookSelection={handleBookSelection}
          onOverrideAmountChange={handleOverrideAmountChange}
          onSelectAll={handleSelectAll}
          onSelectNone={handleSelectNone}
          formatDate={formatDate}
          allowOverride={allowOverride}
        />

        <Message info>
          <Message.Header>
            Selected Amount: ₱{state.totalAmount.toFixed(2)}
            {isOverrideMode && (
              <Label color="orange" size="mini">
                OVERRIDDEN
              </Label>
            )}
          </Message.Header>
          <p>
            {state.selectedBooks.size} of {selectedPatron.loans.length} books
            selected
            {useManualTotal && <span> (Manual total amount)</span>}
          </p>
        </Message>
      </Segment>
    );
  };

  if (!selectedPatron) {
    return null;
  }

  return (
    <Modal open={isOpen} onClose={handleClose} size="large">
      {" "}
      <Modal.Header>
        <Icon name="print" />
        Overdue Notice - {selectedPatron.name}
      </Modal.Header>
      <Modal.Content>
        <div className="overdue-modal-layout">
          <div className="book-selection-column">{renderBookSelection()}</div>

          <div className="overdue-slip-column">
            <OverdueSlipPreview
              selectedPatron={selectedPatron}
              totalAmount={state.totalAmount}
            />
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose}>
          <Icon name="cancel" />
          Close
        </Button>
        <Button
          color="blue"
          onClick={handlePrint}
          disabled={state.selectedBooks.size === 0}
        >
          <Icon name="print" />
          Print Slip (₱{state.totalAmount.toFixed(2)})
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

OverdueSlipModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  selectedPatron: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onOverrideUpdate: PropTypes.func,
  existingOverrideData: PropTypes.object,
  allowOverride: PropTypes.bool,
};

OverdueSlipModal.defaultProps = {
  selectedPatron: null,
  existingOverrideData: null,
  onOverrideUpdate: null,
  allowOverride: true,
};
export default OverdueSlipModal;
